import {
  SOCIAL_FORMAT_LABELS,
  SOCIAL_PIECE_STATUS_LABELS,
  socialIdeaSchema,
  socialPlanListItemSchema,
  socialEventOptionSchema,
  socialPlanSchema,
  type SocialIdea,
  type SocialPieceStatus,
  type SocialEventOption,
  type SocialPlan,
  type SocialPlanListItem,
} from '@growup/site-shared';
import {
  Button,
  Card,
  Checkbox,
  DateInput,
  Field,
  Input,
  Notice,
  Select,
  Textarea,
  type StatusTone,
} from '@growup/ui';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { z } from 'zod';

import { ApiError, getJson, messageOf, send } from '../api';
import { formatDateTime } from '../format';
import { PieceForm } from './PieceForm';
import { PieceView } from './PieceView';
import { PlanFlow } from './PlanFlow';
import { PiecesBoard, PiecesTable, PlanWork } from './PieceViews';

export const PLANS = '/api/site/social/plans';

const dmy = (day: string) => day.split('-').reverse().join('/');

export function periodLabel(p: { startsOn: string | null; endsOn: string | null }): string {
  if (p.startsOn && p.endsOn) return `${dmy(p.startsOn)} a ${dmy(p.endsOn)}`;
  if (p.startsOn) return `Desde ${dmy(p.startsOn)}`;
  if (p.endsOn) return `Até ${dmy(p.endsOn)}`;
  return 'Sem datas';
}

export const PIECE_TONE: Record<SocialPieceStatus, StatusTone> = {
  to_film: 'neutral',
  to_edit: 'info',
  to_approve: 'warning',
  to_publish: 'info',
  published: 'success',
  abandoned: 'neutral',
};

/**
 * Redes sociais > Planos (RF-RS3 a RF-RS8): o gestor ve todos, um influencer
 * so os seus. Abrir um plano mostra os influencers e as pecas.
 */
export function PlansView({
  canManage,
  me,
  initialPlan = null,
}: {
  canManage: boolean;
  me: string;
  /** Abre logo este plano (vindo do Calendario). */
  initialPlan?: string | null;
}) {
  const [list, setList] = useState<SocialPlanListItem[] | null>(null);
  const [open, setOpen] = useState<string | 'new' | null>(initialPlan);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    getJson(PLANS, z.array(socialPlanListItemSchema))
      .then(setList)
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);
  useEffect(load, [load]);

  const close = () => {
    setOpen(null);
    load();
  };

  if (open === 'new') {
    return (
      <div className="flex flex-col gap-4">
        <div>
          <Button variant="ghost" size="sm" onClick={close}>
            ← Planos
          </Button>
        </div>
        <Card headingLevel={2} title="Novo plano">
          <PlanForm
            plan={null}
            onSaved={(p) => {
              setOpen(p.id);
            }}
          />
        </Card>
      </div>
    );
  }
  if (open !== null) {
    return <PlanPage key={open} id={open} me={me} onClose={close} onOpenPlan={setOpen} />;
  }

  return (
    <Card
      headingLevel={2}
      title="Planos"
      actions={
        canManage ? (
          <Button size="sm" onClick={() => setOpen('new')}>
            Novo plano
          </Button>
        ) : null
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-gu-ink-muted">
          {canManage
            ? 'Cada plano junta as peças de conteúdo de um evento, de um mês ou de uma campanha, com os influencers designados.'
            : 'Os planos onde está designado.'}
        </p>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        {list === null && !error ? <p>A carregar…</p> : null}
        {list !== null && list.length === 0 ? <p>Ainda não há planos.</p> : null}
        <ul className="grid gap-3 md:grid-cols-2">
          {list?.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => setOpen(p.id)}
                className="flex w-full flex-col gap-2 border-2 border-gu-line p-3 text-left hover:border-gu-accent"
              >
                <span className="font-semibold">{p.name}</span>
                <span className="text-caption text-gu-ink-muted">
                  {periodLabel(p)} ·{' '}
                  {p.members.length > 0
                    ? p.members.map((m) => m.login).join(', ')
                    : 'sem influencers'}
                </span>
                <Progress summary={p.summary} />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}

export function Progress({ summary }: { summary: SocialPlanListItem['summary'] }) {
  const pct = summary.total === 0 ? 0 : Math.round((summary.published / summary.total) * 100);
  return (
    <span className="flex flex-col gap-1">
      <span className="h-2 w-full bg-gu-sunken" aria-hidden>
        <span className="block h-2 bg-gu-accent" style={{ width: `${String(pct)}%` }} />
      </span>
      <span className="text-caption">
        {summary.published} de {summary.total} publicadas · {summary.filmed} filmadas ·{' '}
        {summary.edited} editadas
        {summary.mustPending > 0
          ? ` · ${String(summary.mustPending)} obrigatórias por publicar`
          : ''}
        {summary.overdue > 0 ? ` · ${String(summary.overdue)} com prazo passado` : ''}
      </span>
    </span>
  );
}

function PlanForm({
  plan,
  onSaved,
}: {
  plan: SocialPlan | null;
  onSaved: (p: SocialPlan) => void;
}) {
  const [name, setName] = useState(plan?.name ?? '');
  const [goal, setGoal] = useState(plan?.goal ?? '');
  const [startsOn, setStartsOn] = useState(plan?.startsOn ?? '');
  const [endsOn, setEndsOn] = useState(plan?.endsOn ?? '');
  const [eventId, setEventId] = useState(plan?.event?.id ?? '');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const payload = {
      name,
      goal,
      startsOn: startsOn === '' ? null : startsOn,
      endsOn: endsOn === '' ? null : endsOn,
      eventId: eventId === '' ? null : eventId,
    };
    setBusy(true);
    setError(null);
    setFields({});
    try {
      const res = plan
        ? await send('PUT', `${PLANS}/${plan.id}`, payload)
        : await send('POST', PLANS, payload);
      onSaved(socialPlanSchema.parse(res));
    } catch (err) {
      setError(messageOf(err));
      if (err instanceof ApiError) setFields(err.fields);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="flex flex-col gap-4" onSubmit={(e) => void submit(e)} noValidate>
      <Field label="Nome do plano" hint="Ex.: Torneio de Outono, Novembro 2026" error={fields.name}>
        <Input value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field
        label="Objetivo"
        optional
        hint="Ex.: 10 peças, 3 por dia do evento"
        error={fields.goal}
      >
        <Textarea
          rows={2}
          value={goal}
          maxLength={1000}
          onChange={(e) => setGoal(e.target.value)}
        />
      </Field>
      <Field
        label="Evento"
        optional
        hint="Para conteúdo feito durante um evento. Sem datas, o plano fica com as do evento."
        error={fields.eventId}
      >
        <EventSelect value={eventId} onChange={setEventId} />
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Início" optional error={fields.startsOn}>
          <DateInput value={startsOn} onChange={setStartsOn} />
        </Field>
        <Field label="Fim" optional error={fields.endsOn}>
          <DateInput value={endsOn} onChange={setEndsOn} />
        </Field>
      </div>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <div>
        <Button type="submit" disabled={busy}>
          {plan ? 'Guardar' : 'Criar plano'}
        </Button>
      </div>
    </form>
  );
}

/** Os eventos que se podem ligar (gestor). */
function EventSelect({
  value,
  onChange,
  id,
}: {
  value: string;
  onChange: (id: string) => void;
  id?: string;
}) {
  const [events, setEvents] = useState<SocialEventOption[]>([]);
  useEffect(() => {
    getJson('/api/site/social/events', z.array(socialEventOptionSchema))
      .then(setEvents)
      .catch(() => setEvents([]));
  }, []);
  return (
    <Select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Sem evento</option>
      {events.map((e) => (
        <option key={e.id} value={e.id}>
          {e.title} ({periodLabel(e)})
        </option>
      ))}
    </Select>
  );
}

/** Copiar o plano (RF-RS6): as pecas por fazer, sem pessoas. */
function CopyForm({
  plan,
  onCopied,
  onCancel,
}: {
  plan: SocialPlan;
  onCopied: (p: SocialPlan) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(`${plan.name} (cópia)`);
  const [eventId, setEventId] = useState('');
  const [startsOn, setStartsOn] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setFields({});
    try {
      const res = await send('POST', `${PLANS}/${plan.id}/copy`, {
        name,
        eventId: eventId === '' ? null : eventId,
        startsOn: startsOn === '' ? null : startsOn,
      });
      onCopied(socialPlanSchema.parse(res));
    } catch (err) {
      setError(messageOf(err));
      if (err instanceof ApiError) setFields(err.fields);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="flex flex-col gap-4" onSubmit={(e) => void submit(e)} noValidate>
      <p className="text-gu-ink-muted">
        Copia as peças que não foram abandonadas, por fazer e sem pessoas, com as datas à mesma
        distância do início. Com evento, os dias vão pela ordem e as zonas pelo nome.
      </p>
      <Field label="Nome do plano novo" error={fields.name}>
        <Input value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Evento" optional error={fields.eventId}>
          <EventSelect value={eventId} onChange={setEventId} />
        </Field>
        <Field label="Início" optional hint="Sem início, fica o do evento." error={fields.startsOn}>
          <DateInput value={startsOn} onChange={setStartsOn} />
        </Field>
      </div>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={busy}>
          Copiar plano
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}

type Mode = 'view' | 'edit' | 'copy' | 'piece' | 'ideas';

function PlanPage({
  id,
  me,
  onClose,
  onOpenPlan,
}: {
  id: string;
  me: string;
  onClose: () => void;
  onOpenPlan: (id: string) => void;
}) {
  const [plan, setPlan] = useState<SocialPlan | null>(null);
  const [mode, setMode] = useState<Mode>('view');
  const [view, setView] = useState<'lista' | 'quadro'>('lista');
  const [filter, setFilter] = useState<SocialPieceStatus | null>(null);
  const [pieceId, setPieceId] = useState<string | null>(null);
  const [identifier, setIdentifier] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getJson(`${PLANS}/${id}`, socialPlanSchema)
      .then(setPlan)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [id]);

  const run = async (method: string, path: string, body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      setPlan(socialPlanSchema.parse(await send(method, `${PLANS}/${id}${path}`, body)));
      return true;
    } catch (e) {
      setError(messageOf(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const back = (
    <div>
      <Button variant="ghost" size="sm" onClick={onClose}>
        ← Planos
      </Button>
    </div>
  );

  if (!plan) {
    return (
      <div className="flex flex-col gap-4">
        {back}
        {error ? <Notice tone="danger">{error}</Notice> : <p>A carregar…</p>}
      </div>
    );
  }

  const piece = plan.pieces.find((p) => p.id === pieceId);
  if (piece) {
    return (
      <PieceView
        plan={plan}
        piece={piece}
        me={me}
        onChange={setPlan}
        onClose={() => setPieceId(null)}
      />
    );
  }

  const shown = filter ? { ...plan, pieces: plan.pieces.filter((p) => p.status === filter) } : plan;

  const remove = async () => {
    if (!window.confirm(`Apagar o plano «${plan.name}» e as suas peças? Não se pode desfazer.`))
      return;
    setBusy(true);
    try {
      await send('DELETE', `${PLANS}/${id}`);
      onClose();
    } catch (e) {
      setError(messageOf(e));
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {back}
      <Card
        headingLevel={2}
        title={plan.name}
        actions={
          plan.canManage && mode === 'view' ? (
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" onClick={() => setMode('edit')}>
                Editar plano
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setMode('copy')}>
                Copiar
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => void remove()}>
                Apagar
              </Button>
            </div>
          ) : null
        }
      >
        {mode === 'copy' ? (
          <CopyForm
            plan={plan}
            onCopied={(p) => onOpenPlan(p.id)}
            onCancel={() => setMode('view')}
          />
        ) : mode === 'edit' ? (
          <PlanForm
            plan={plan}
            onSaved={(p) => {
              setPlan(p);
              setMode('view');
            }}
          />
        ) : (
          <div className="flex flex-col gap-3">
            <p className="text-caption text-gu-ink-muted">
              {plan.event ? `Evento: ${plan.event.title} · ` : ''}
              {periodLabel(plan)} · última alteração {formatDateTime(plan.updatedAt)} por{' '}
              {plan.updatedBy}
            </p>
            {plan.goal ? <p className="whitespace-pre-line">{plan.goal}</p> : null}
            <PlanFlow pieces={plan.pieces} filter={filter} onFilter={setFilter} />
          </div>
        )}
      </Card>

      <Card headingLevel={2} title="Influencers">
        <div className="flex flex-col gap-3">
          {plan.members.length === 0 ? <p>Ainda ninguém designado.</p> : null}
          <ul className="flex flex-wrap gap-2">
            {plan.members.map((m) => (
              <li
                key={m.accountId}
                className="flex items-center gap-1 border-2 border-gu-line px-2 py-1"
              >
                {m.login}
                {plan.canManage ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Tirar ${m.login} do plano`}
                    disabled={busy}
                    onClick={() => {
                      if (
                        window.confirm(`Tirar ${m.login} do plano? O que já fez fica registado.`)
                      ) {
                        void run('DELETE', `/members/${m.accountId}`);
                      }
                    }}
                  >
                    ✕
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
          {plan.canManage ? (
            <form
              className="flex flex-wrap items-end gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                void run('POST', '/members', { identifier }).then((ok) => {
                  if (ok) setIdentifier('');
                });
              }}
            >
              <Field label="Designar influencer" hint="Login ou email; recebe um email.">
                <Input value={identifier} onChange={(e) => setIdentifier(e.target.value)} />
              </Field>
              <Button
                type="submit"
                variant="secondary"
                disabled={busy || identifier.trim().length < 2}
              >
                Designar
              </Button>
            </form>
          ) : null}
        </div>
      </Card>

      <Card
        headingLevel={2}
        title="Peças"
        actions={
          <div className="flex flex-wrap gap-2">
            <nav className="flex gap-1" aria-label="Vista das peças">
              {(['lista', 'quadro'] as const).map((v) => (
                <Button
                  key={v}
                  size="sm"
                  variant={view === v ? 'primary' : 'ghost'}
                  aria-pressed={view === v}
                  onClick={() => setView(v)}
                >
                  {v === 'lista' ? 'Lista' : 'Quadro'}
                </Button>
              ))}
            </nav>
            {plan.canManage && mode === 'view' ? (
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" onClick={() => setMode('ideas')}>
                  Do banco de ideias
                </Button>
                <Button size="sm" onClick={() => setMode('piece')}>
                  Peça nova
                </Button>
              </div>
            ) : null}
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          {error ? <Notice tone="danger">{error}</Notice> : null}
          {mode === 'piece' ? (
            <PieceForm
              piece={null}
              event={plan.event}
              zones={plan.zones}
              submitLabel="Juntar ao plano"
              onCancel={() => setMode('view')}
              onSubmit={async (payload) => {
                setPlan(
                  socialPlanSchema.parse(await send('POST', `${PLANS}/${id}/pieces`, payload)),
                );
                setMode('view');
              }}
            />
          ) : null}
          {mode === 'ideas' ? (
            <IdeaPicker
              onCancel={() => setMode('view')}
              onPick={async (ideaIds) => {
                if (await run('POST', '/pieces/from-ideas', { ideaIds })) setMode('view');
              }}
            />
          ) : null}
          {plan.pieces.length === 0 ? <p>Ainda não há peças.</p> : null}
          {filter ? (
            <p className="text-caption">
              Só as peças «{SOCIAL_PIECE_STATUS_LABELS[filter]}» ({shown.pieces.length}).{' '}
              <button type="button" className="underline" onClick={() => setFilter(null)}>
                Mostrar todas
              </button>
            </p>
          ) : null}
          {plan.pieces.length > 0 && view === 'lista' ? (
            <PiecesTable pieces={shown.pieces} onOpen={setPieceId} />
          ) : null}
          {plan.pieces.length > 0 && view === 'quadro' ? (
            <PiecesBoard
              plan={shown}
              busy={busy}
              onOpen={setPieceId}
              onAdvance={(a) => void run(a.method, a.path, a.body)}
            />
          ) : null}
        </div>
      </Card>

      <Card headingLevel={2} title="Quem fez o quê">
        <PlanWork plan={plan} />
      </Card>
    </div>
  );
}

function IdeaPicker({
  onPick,
  onCancel,
}: {
  onPick: (ids: string[]) => Promise<void>;
  onCancel: () => void;
}) {
  const [ideas, setIdeas] = useState<SocialIdea[] | null>(null);
  const [chosen, setChosen] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getJson('/api/site/social/ideas', z.array(socialIdeaSchema))
      .then((all) => setIdeas(all.filter((i) => i.status === 'accepted')))
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);

  return (
    <div className="flex flex-col gap-3 border-2 border-gu-line p-3">
      <p className="text-gu-ink-muted">
        As ideias escolhidas copiam-se para o plano: mudar o banco depois não mexe nelas.
      </p>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {ideas === null && !error ? <p>A carregar…</p> : null}
      {ideas?.length === 0 ? <p>O banco de ideias está vazio.</p> : null}
      <div className="flex max-h-80 flex-col gap-2 overflow-y-auto">
        {ideas?.map((i) => (
          <Checkbox
            key={i.id}
            label={`${i.title} (${SOCIAL_FORMAT_LABELS[i.format]})`}
            checked={chosen.includes(i.id)}
            onChange={(e) =>
              setChosen((c) => (e.target.checked ? [...c, i.id] : c.filter((x) => x !== i.id)))
            }
          />
        ))}
      </div>
      <div className="flex gap-2">
        <Button disabled={chosen.length === 0} onClick={() => void onPick(chosen)}>
          Juntar {chosen.length > 0 ? String(chosen.length) : ''} ao plano
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}
